const CURRENT_YEAR = new Date().getUTCFullYear();

export const DEFAULT_AS_OF_DATE = new Date().toISOString().slice(0, 10);

export const DEFAULT_GLOBAL_SCHEDULE_DATES = [
    `${CURRENT_YEAR}-03-09`,
    `${CURRENT_YEAR}-06-01`,
    `${CURRENT_YEAR}-12-01`,
];

const MONTH_TEMPLATES = {
    3: [3, 6, 12],
    4: [3, 6, 9, 12],
    6: [2, 4, 6, 8, 10, 12],
    12: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

export const CADENCE_OPTIONS = [
    { value: "global", label: "Use global 3-buy cadence" },
    { value: "4", label: "Quarterly · 4 buys" },
    { value: "6", label: "Bi-monthly · 6 buys" },
    { value: "12", label: "Monthly · 12 buys" },
];

export function normalizeIsoDate(value, fallback) {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : fallback;
}

function getYearFromDates(dates) {
    const parsedYear = Number(dates[0]?.slice(0, 4));
    return Number.isFinite(parsedYear) && parsedYear > 2000 ? parsedYear : CURRENT_YEAR;
}

function padMonth(value) {
    return String(value).padStart(2, "0");
}

function monthKeyFromIso(value) {
    return value.slice(5, 7);
}

export function isSupplierCadenceFrequency(value) {
    return value === 3 || value === 4 || value === 6 || value === 12;
}

function sanitizeGlobalScheduleDates(value) {
    const dates = Array.isArray(value) ? value : [];
    return DEFAULT_GLOBAL_SCHEDULE_DATES.map((fallback, index) => {
        const nextValue = typeof dates[index] === "string" ? dates[index] : fallback;
        return normalizeIsoDate(nextValue, fallback);
    });
}

export function buildCadenceDates(frequency, globalDates) {
    const normalizedGlobal = DEFAULT_GLOBAL_SCHEDULE_DATES.map((fallback, index) =>
        normalizeIsoDate(globalDates[index], fallback)
    );
    if (frequency === 3) {
        return normalizedGlobal;
    }

    const year = getYearFromDates(normalizedGlobal);
    const globalByMonth = new Map(normalizedGlobal.map((date) => [monthKeyFromIso(date), date]));

    return MONTH_TEMPLATES[frequency].map((month) => {
        const monthKey = padMonth(month);
        const fromGlobal = globalByMonth.get(monthKey);
        if (fromGlobal) return fromGlobal;
        return `${year}-${monthKey}-01`;
    });
}

function ensureOverrideFrequencyDates(override, globalDates) {
    if (!override) return undefined;
    const defaults = buildCadenceDates(override.frequency, globalDates);
    return {
        frequency: override.frequency,
        dates: defaults.map((fallback, index) => normalizeIsoDate(override.dates[index], fallback)),
    };
}

export function statefulSupplierOverride(override, globalDates) {
    return (
        ensureOverrideFrequencyDates(override, globalDates) ?? {
            frequency: 3,
            dates: buildCadenceDates(3, globalDates),
        }
    );
}

export function buildResetPoScheduleState() {
    return {
        asOfDate: DEFAULT_AS_OF_DATE,
        globalScheduleDates: [...DEFAULT_GLOBAL_SCHEDULE_DATES],
        supplierOverrides: {},
    };
}

export function parseIsoDate(value) {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatPoScheduleDate(value) {
    const parsed = parseIsoDate(value);
    if (!parsed) return "—";
    return parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

export function formatPoScheduleCurrency(value) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(value || 0);
}

export function formatPoScheduleNumber(value) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value || 0);
}

export function cadenceLabel(frequency) {
    if (frequency === 12) return "Monthly";
    if (frequency === 6) return "Bi-monthly";
    if (frequency === 4) return "Quarterly";
    return "Core 3-buy";
}

export function approvalTone(approvedCount, pendingCount) {
    if (approvedCount > 0 && pendingCount === 0) return "border-transparent bg-emerald-50 text-emerald-700";
    if (approvedCount > 0 && pendingCount > 0) return "border-transparent bg-amber-50 text-amber-700";
    return "border-transparent bg-slate-100 text-slate-700";
}

export function effectiveCadenceValue(value) {
    return value ? String(value) : "global";
}

export function findScheduleAssignment(requiredByDate, scheduleDates) {
    const requiredTime = parseIsoDate(requiredByDate)?.getTime();
    if (!requiredTime) {
        return {
            scheduledPoDate: requiredByDate,
            scheduleType: "Interim",
        };
    }

    const normalizedDates = [...scheduleDates]
        .filter((date) => parseIsoDate(date))
        .sort(
            (left, right) =>
                (parseIsoDate(left)?.getTime() ?? 0) - (parseIsoDate(right)?.getTime() ?? 0)
        );

    let selectedDate = null;
    for (const date of normalizedDates) {
        const scheduleTime = parseIsoDate(date)?.getTime() ?? 0;
        if (scheduleTime <= requiredTime) {
            selectedDate = date;
        }
    }

    if (selectedDate) {
        return {
            scheduledPoDate: selectedDate,
            scheduleType: "Planned",
        };
    }

    return {
        scheduledPoDate: requiredByDate,
        scheduleType: "Interim",
    };
}

export function isPoScheduleApproved(value) {
    return value === true || value === "true";
}

function sanitizeFilePart(value) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "supplier";
}

export function downloadPoScheduleCsv(rows, supplier) {
    const headers = [
        "Supplier",
        "Product",
        "Supply SKU",
        "Category",
        "Sub Category",
        "Schedule Type",
        "Scheduled PO Date",
        "Required By",
        "Units To Order",
        "Cash Required",
        "Approval Status",
        "Material Planning Note",
    ];

    const csv = [headers, ...rows]
        .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `eadin-po-schedule-${sanitizeFilePart(supplier)}-sku-detail.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

export function compareScheduleLines(left, right, asOfDate) {
    const asOfTime = parseIsoDate(asOfDate)?.getTime() ?? 0;
    const leftTime = parseIsoDate(left.scheduledPoDate)?.getTime() ?? 0;
    const rightTime = parseIsoDate(right.scheduledPoDate)?.getTime() ?? 0;
    const leftUpcoming = leftTime >= asOfTime;
    const rightUpcoming = rightTime >= asOfTime;

    if (leftUpcoming !== rightUpcoming) {
        return leftUpcoming ? -1 : 1;
    }

    if (leftUpcoming && rightUpcoming) {
        return leftTime - rightTime || left.supplier.localeCompare(right.supplier);
    }

    return rightTime - leftTime || left.supplier.localeCompare(right.supplier);
}
